import { useState } from 'react';
import { useLang } from '../hooks/useLang';
import { ArrowLeft, Download, Leaf, Zap, Package, Link2, RefreshCw, ShieldCheck, BarChart3, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kober-energy/artifacts/vys7c9cr_3.png";
const PDF_URL  = "/KOBER-Climate-Policy-2026.pdf";

// Animation variants — module-scope to avoid new object refs on every render
const ANIM_HERO    = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const ANIM_TARGETS = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.1 } };
const ANIM_COMMITS = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.2 } };
const ANIM_APPLY   = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.3 } };

const TARGETS = {
  EN: [
    { value: "≥ 3 GWh", label: "Storage commissioned in Romania by 2029" },
    { value: "2030",    label: "Operational Net-Zero (Scope 1 & 2)" },
    { value: "−30%",    label: "Embodied carbon per kWh by 2030 vs. 2026" },
    { value: "2045",    label: "Net-Zero Value Chain (SBTi Standard)" },
  ],
  RO: [
    { value: "≥ 3 GWh", label: "Capacitate de stocare pusă în funcțiune în România până în 2029" },
    { value: "2030",    label: "Net-Zero Operațional (Scope 1 & 2)" },
    { value: "−30%",    label: "Carbon incorporat per kWh până în 2030 față de 2026" },
    { value: "2045",    label: "Net-Zero Lanț de Valoare (Standard SBTi)" },
  ],
};

const COMMITMENTS = {
  EN: [
    {
      icon: Zap,
      heading: "1. We decarbonise the energy system",
      body: "We will commission at least 3 GWh of storage capacity in Romania by the end of 2029 and operate the assets so as to maximise the renewable energy absorbed and the fossil generation displaced. We report avoided emissions annually, calculated and verified separately from our inventory.",
    },
    {
      icon: Leaf,
      heading: "2. We reach operational net zero in 2030",
      body: "We eliminate Scope 1 and 2 emissions through 100% renewable energy (with progressive hourly matching), full electrification of processes in the Brăila factories, an electric fleet and the elimination of SF6 from all new switchgear, ahead of Regulation (EU) 2024/573.",
    },
    {
      icon: Package,
      heading: "3. We manufacture with declared carbon",
      body: "Every battery, power conversion system and transformer produced by KÖBER carries a carbon footprint calculated under ISO 14067 / EN 50693 and the EU methodology, verified by a third party, and a digital battery passport in accordance with Regulation (EU) 2023/1542 — from the first unit delivered.",
    },
    {
      icon: Link2,
      heading: "4. We reduce carbon in the supply chain",
      body: "We apply carbon-footprint thresholds in cell procurement (≤ 50 kgCO₂e/kWh from 2028), recycled-content requirements, due diligence for critical raw materials and an internal carbon price (€80/t, then €120/t from 2030) in all investment decisions.",
    },
    {
      icon: RefreshCw,
      heading: "5. We design for circularity",
      body: "Augmentation instead of replacement, design for disassembly, second life and 100% end-of-life collection and recycling, with authorised European partners.",
    },
    {
      icon: ShieldCheck,
      heading: "6. We build resilience",
      body: "We assess the physical climate risks of every site before the investment decision and size our assets for the climate conditions of the 2040s.",
    },
    {
      icon: BarChart3,
      heading: "7. We govern and report transparently",
      body: "Climate responsibility is anchored at the level of the Board of Directors and linked to executive remuneration. We publish an annual report aligned with ESRS E1 / IFRS S2, with external assurance, and submit our targets for SBTi validation — even though legislation does not yet require us to.",
    },
    {
      icon: Globe,
      heading: "8. We align with European and global frameworks",
      body: "The European Climate Law (−90% by 2040), the Batteries Regulation, the Net-Zero Industry Act, the EU Taxonomy, CBAM, the electricity market reform, Romania's NECP and the Paris Agreement are the benchmarks against which we measure our progress.",
    },
  ],
  RO: [
    {
      icon: Zap,
      heading: "1. Decarbonizăm sistemul energetic",
      body: "Vom pune în funcțiune cel puțin 3 GWh de capacitate de stocare în România până la sfârșitul anului 2029 și vom opera activele astfel încât să maximizăm energia regenerabilă absorbită și generarea pe bază de combustibili fosili înlocuită. Raportăm anual emisiile evitate, calculate și verificate separat față de inventarul nostru.",
    },
    {
      icon: Leaf,
      heading: "2. Atingem net-zero operațional în 2030",
      body: "Eliminăm emisiile Scope 1 și 2 prin 100% energie regenerabilă (cu potrivire orară progresivă), electrificarea completă a proceselor din fabricile din Brăila, o flotă electrică și eliminarea SF6 din toate echipamentele noi de comutație, înaintea Regulamentului (UE) 2024/573.",
    },
    {
      icon: Package,
      heading: "3. Producem cu carbon declarat",
      body: "Fiecare baterie, sistem de conversie a puterii și transformator produs de KÖBER poartă o amprentă de carbon calculată conform ISO 14067 / EN 50693 și metodologiei UE, verificată de o terță parte, și un pașaport digital al bateriei în conformitate cu Regulamentul (UE) 2023/1542 — de la prima unitate livrată.",
    },
    {
      icon: Link2,
      heading: "4. Reducem carbonul în lanțul de aprovizionare",
      body: "Aplicăm praguri de amprentă de carbon în achiziționarea celulelor (≤ 50 kgCO₂e/kWh din 2028), cerințe de conținut reciclat, due diligence pentru materii prime critice și un preț intern al carbonului (€80/t, apoi €120/t din 2030) în toate deciziile de investiții.",
    },
    {
      icon: RefreshCw,
      heading: "5. Proiectăm pentru circularitate",
      body: "Augmentare în loc de înlocuire, proiectare pentru dezasamblare, a doua viață și 100% colectare și reciclare la sfârșitul ciclului de viață, cu parteneri europeni autorizați.",
    },
    {
      icon: ShieldCheck,
      heading: "6. Construim reziliență",
      body: "Evaluăm riscurile climatice fizice ale fiecărui amplasament înainte de decizia de investiție și dimensionăm activele noastre pentru condițiile climatice ale anilor 2040.",
    },
    {
      icon: BarChart3,
      heading: "7. Guvernăm și raportăm transparent",
      body: "Responsabilitatea climatică este ancorată la nivelul Consiliului de Administrație și legată de remunerarea executivilor. Publicăm un raport anual aliniat cu ESRS E1 / IFRS S2, cu asigurare externă, și ne supunem obiectivele validării SBTi — chiar dacă legislația nu ne cere încă acest lucru.",
    },
    {
      icon: Globe,
      heading: "8. Ne aliniem cu cadrele europene și globale",
      body: "Legea europeană a climei (−90% până în 2040), Regulamentul bateriilor, Legea industriei net-zero, Taxonomia UE, CBAM, reforma pieței de electricitate, PNEC al României și Acordul de la Paris sunt reperele față de care ne măsurăm progresul.",
    },
  ],
};

const UI = {
  EN: {
    back: "Back to Home",
    category: "Sustainability",
    title: "Climate & Decarbonisation Policy",
    edition: "Edition 1.0 · September 2026 · Approved by the Board of Directors",
    intro: "KÖBER Energy Holding recognises that climate change is the defining risk of this century and that its activity is, by its nature, part of the solution. Through this statement, KÖBER Energy Holding publicly commits to the following undertakings.",
    targetsTitle: "Our Climate Targets",
    commitmentsTitle: "Our Commitments",
    application: "Application",
    applicationBody: "This policy applies to all entities controlled by KÖBER Energy Holding, its employees, contractors and business partners acting on its behalf. It is detailed in the Climate & Decarbonisation Strategy 2026–2035, the Supplier Code and related internal procedures. It is reviewed annually by the Board's Sustainability & Climate Committee.",
    download: "Download Policy PDF",
    contact: "Sustainability enquiries: office@koberholding.com",
    copyright: "© KOBER Energy. All rights reserved.",
  },
  RO: {
    back: "Înapoi acasă",
    category: "Sustenabilitate",
    title: "Politica Climatică & de Decarbonizare",
    edition: "Ediția 1.0 · Septembrie 2026 · Aprobată de Consiliul de Administrație",
    intro: "KÖBER Energy Holding recunoaște că schimbările climatice reprezintă riscul definitoriu al acestui secol și că activitatea sa este, prin natura sa, parte a soluției. Prin această declarație, KÖBER Energy Holding se angajează public la următoarele.",
    targetsTitle: "Obiectivele Noastre Climatice",
    commitmentsTitle: "Angajamentele Noastre",
    application: "Aplicabilitate",
    applicationBody: "Această politică se aplică tuturor entităților controlate de KÖBER Energy Holding, angajaților, contractorilor și partenerilor de afaceri care acționează în numele său. Este detaliată în Strategia Climatică & de Decarbonizare 2026–2035, Codul Furnizorilor și procedurile interne aferente. Este revizuită anual de Comitetul de Sustenabilitate & Climă al Consiliului.",
    download: "Descarcă Politica PDF",
    contact: "Întrebări privind sustenabilitatea: office@koberholding.com",
    copyright: "© KOBER Energy. Toate drepturile rezervate.",
  },
};

export default function SustainabilityPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useLang();
  const handleSetLang = setLang;

  const ui   = UI[lang];
  const targets     = TARGETS[lang];
  const commitments = COMMITMENTS[lang];

  return (
    <div className="bg-[#050505] min-h-screen text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Gold top bar */}
      <div className="h-0.5 bg-gradient-to-r from-[#D4A017] via-[#D4A017]/40 to-transparent" />

      {/* Header */}
      <header className="bg-black/60 backdrop-blur-xl border-b border-white/8 sticky top-0 z-40">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl h-20 flex items-center justify-between">
          <button onClick={() => navigate('/')} data-testid="sustainability-back-btn"
            className="flex items-center gap-2.5 text-sm text-white/50 hover:text-[#D4A017] transition-colors duration-200 group">
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
            {ui.back}
          </button>
          <img src={LOGO_URL} alt="KÖBER Energy Holding" className="h-10 w-auto" />
          <div className="flex items-center gap-1 text-sm" data-testid="sustainability-lang-toggle">
            {['EN', 'RO'].map((l, i) => (
              <span key={l} className="flex items-center gap-1">
                {i > 0 && <span className="text-white/20 text-xs">/</span>}
                <button onClick={() => handleSetLang(l)}
                  className={`px-2 py-1 font-medium transition-colors ${lang === l ? 'text-[#D4A017]' : 'text-white/40 hover:text-white/70'}`}>
                  {l}
                </button>
              </span>
            ))}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 lg:px-12 max-w-5xl py-16 sm:py-24">

        {/* Hero */}
        <motion.div {...ANIM_HERO}
          className="mb-14 pb-10 border-b border-white/8">
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{ui.category}</p>
          <h1 className="text-4xl sm:text-5xl font-medium text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {ui.title}
          </h1>
          <p className="text-sm text-white/35 mb-6">{ui.edition}</p>
          <p className="text-sm text-white/60 leading-relaxed max-w-3xl mb-8">{ui.intro}</p>
          <a href={PDF_URL} download data-testid="sustainability-download-btn"
            className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#E5B020] text-black text-sm font-semibold px-6 py-3 transition-colors duration-200">
            <Download size={14} />
            {ui.download}
          </a>
        </motion.div>

        {/* Targets */}
        <motion.div {...ANIM_TARGETS} className="mb-16">
          <h2 className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-8 font-medium">{ui.targetsTitle}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {targets.map((t) => (
              <div key={t.value} className="bg-[#0C0C0C] border border-white/8 p-6">
                <p className="text-3xl font-bold text-[#D4A017] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.value}</p>
                <p className="text-xs text-white/50 leading-snug">{t.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Commitments */}
        <motion.div {...ANIM_COMMITS}>
          <h2 className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-10 font-medium">{ui.commitmentsTitle}</h2>
          <div className="space-y-0">
            {commitments.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.heading} className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-10 py-10 border-b border-white/6 last:border-0">
                  <div className="lg:col-span-1 flex items-start gap-3">
                    <div className="mt-0.5 w-7 h-7 rounded-sm bg-[#D4A017]/10 border border-[#D4A017]/20 flex items-center justify-center flex-shrink-0">
                      <Icon size={13} className="text-[#D4A017]" />
                    </div>
                    <h3 className="text-sm font-medium text-[#D4A017] leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}
                      data-testid={`sustainability-commitment-${i}`}>
                      {item.heading}
                    </h3>
                  </div>
                  <div className="lg:col-span-3">
                    <p className="text-sm text-white/65 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Application */}
        <motion.div {...ANIM_APPLY}
          className="mt-10 grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-10 py-10 border-t border-white/8">
          <div className="lg:col-span-1">
            <h3 className="text-sm font-medium text-[#D4A017]" style={{ fontFamily: "'Outfit', sans-serif" }}>{ui.application}</h3>
          </div>
          <div className="lg:col-span-3">
            <p className="text-sm text-white/65 leading-relaxed">{ui.applicationBody}</p>
          </div>
        </motion.div>

        {/* Footer row */}
        <div className="mt-10 pt-8 border-t border-white/8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-white/25">{ui.contact}</p>
          <p className="text-xs text-white/25">{ui.copyright}</p>
        </div>
      </main>
    </div>
  );
}
